import type { CSSProperties, FC } from "react"
import { useMemo } from "react"
import classNames from "classnames"
import type { WithCapsuleTextFragment } from "~/contentful"
import ContentfulRichTextRenderer from "../ContentfulRichTextRenderer"
import Layout from "~/components/capsules/Layout"
import type { CapsuleProps } from "~/components/capsules"

interface Props extends CapsuleProps {
  capsule: WithCapsuleTextFragment
}

const HEADING_LEVELS = ["h1", "h2", "h3", "h4"] as const

const isHeadingLevel = (
  headingLevel: string | null | undefined
): headingLevel is (typeof HEADING_LEVELS)[number] =>
  !!headingLevel &&
  HEADING_LEVELS.includes(headingLevel as (typeof HEADING_LEVELS)[number])

const getHeadingLevel = (
  headingLevel: string | null | undefined
): "h1" | "h2" | "h3" | "h4" => {
  if (isHeadingLevel(headingLevel)) {
    return headingLevel
  }

  return "h2"
}

const Text: FC<Props> = ({ capsule, page }) => {
  const classes = useMemo(
    () => ({
      unorderedListClass: "list-disc ml-4 mb-4",
      paragraphClass:
        "mb-4 last:mb-0 text-base lg:text-lg leading-relaxed text-pretty",
      hyperlinkClass: "link link-red",
      heading3Class: `mb-6 font-serif lg:text-xl ${
        page.textColorVariant === "light"
          ? "text-camp-capsule-page-text-light"
          : "text-camp-black"
      }`
    }),
    [page.textColorVariant]
  )

  const styles = useMemo<{ hyperlinkStyles: CSSProperties }>(
    () => ({
      hyperlinkStyles: {
        color: capsule.linkColor || undefined
      }
    }),
    [capsule.linkColor]
  )

  return (
    <Layout
      title={
        capsule.doNotDisplayTitle
          ? undefined
          : {
              content: capsule.title,
              variant: page.textColorVariant,
              headingLevel: getHeadingLevel(capsule.headingLevel),
              color: capsule.color
            }
      }
    >
      {capsule.text?.json && (
        <div
          className={classNames(
            "mx-auto my-3 max-w-lg text-pretty lg:my-6 lg:max-w-3xl lg:text-lg",
            page.textColorVariant === "light"
              ? "text-camp-capsule-page-text-light"
              : "text-gray-300",
            {
              "text-center":
                !capsule.alignment || capsule.alignment === "center",
              "text-right": capsule.alignment === "right",
              "text-left": capsule.alignment === "left"
            }
          )}
          style={{
            color: capsule.color ?? undefined
          }}
        >
          <ContentfulRichTextRenderer
            data={capsule.text.json}
            classes={classes}
            styles={styles}
            origin={capsule.title ?? page.name ?? ""}
            openLinksInNewWindow={capsule.openLinksInNewWindow ?? false}
          />
        </div>
      )}
    </Layout>
  )
}

export default Text
